import { useState } from "react";
import { theme, type as text } from "../../theme";
import { api, apiErrorMessage } from "../../api/client";
import { PortalLink } from "../../components/PortalLink";
import { ConfidencePill, type ExposureConfidence } from "../../components/AdvisoryAffectedAssets";

// One line under an asset's name on the register: how many threat advisories reach it
// (confirmed / likely — backend services/advisoryExposure.ts) and how many CVEs Defender
// reports on it. Data: GET /threat-feeds/asset-exposure for the whole register; the
// expanded list comes from GET /threat-feeds/assets/:id when the line is opened.

export interface AssetExposure { confirmed: number; likely: number; cves: string[]; defenderVulnerabilities: number }
export type AssetExposureMap = Record<string, AssetExposure>;

interface AssetExposureDetail {
  exposures: Array<{ id: string; cveId: string | null; confidence: ExposureConfidence; basis: string; reason: string; firstSeenAt: string; advisory: { id: string; source: string; externalId: string; title: string; severity: string | null; url: string | null } }>;
  vulnerabilities: Array<{ cveId: string; productName: string; productVendor: string | null; productVersion: string | null; severity: string | null; fixingKbId: string | null; firstSeenAt: string; source: string }>;
  software: Array<{ name: string; publisher: string | null; version: string | null; source: string }>;
}

export function AssetExposureNote({ assetId, exposure }: { assetId: string; exposure: AssetExposure }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<AssetExposureDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const advisories = exposure.confirmed + exposure.likely;
  if (advisories === 0 && exposure.defenderVulnerabilities === 0) return null;
  const tone = exposure.confirmed > 0 ? theme.color.dangerText : advisories > 0 ? theme.color.warningText : theme.color.muted;

  async function toggle() {
    const next = !open;
    setOpen(next);
    if (!next || detail) return;
    try { setDetail(await api.get<AssetExposureDetail>(`/threat-feeds/assets/${assetId}`)); }
    catch (e) { setError(apiErrorMessage(e, "Could not load the exposure")); }
  }

  const parts = [
    advisories > 0 ? `Exposed to ${advisories} advisor${advisories === 1 ? "y" : "ies"}${exposure.confirmed ? ` (${exposure.confirmed} confirmed)` : ""}` : null,
    exposure.defenderVulnerabilities > 0 ? `Defender: ${exposure.defenderVulnerabilities} open CVE${exposure.defenderVulnerabilities === 1 ? "" : "s"}` : null,
  ].filter(Boolean);

  return (
    <div style={{ ...text.meta, fontWeight: 400 }}>
      <button type="button" onClick={toggle} aria-expanded={open} style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer", color: tone, fontWeight: 600, fontSize: text.meta.fontSize }}>
        {parts.join(" · ")} {open ? "▴" : "▾"}
      </button>
      {open && (
        <div style={{ marginTop: 6, display: "grid", gap: 6, padding: "8px 10px", border: `1px solid ${theme.color.border}`, borderRadius: 3, background: theme.color.panelAlt, maxWidth: 560 }}>
          {error && <span style={{ color: theme.color.dangerText }}>{error}</span>}
          {!detail && !error && <span style={{ color: theme.color.muted }}>Loading…</span>}
          {detail && detail.exposures.length > 0 && (
            <div style={{ display: "grid", gap: 4 }}>
              <span style={text.eyebrow}>Threat advisories reaching this asset</span>
              {detail.exposures.map((e) => (
                <div key={e.id} style={{ display: "grid", gap: 1 }}>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                    <ConfidencePill confidence={e.confidence} />
                    {e.cveId && <span style={{ ...text.ref, color: theme.color.heading }}>{e.cveId}</span>}
                    <span style={{ color: theme.color.text }}>{e.advisory.title}</span>
                    <span style={{ color: theme.color.faint }}>{e.advisory.source}</span>
                  </div>
                  <span style={{ color: theme.color.muted }}>{e.reason}</span>
                </div>
              ))}
            </div>
          )}
          {detail && detail.vulnerabilities.length > 0 && (
            <div style={{ display: "grid", gap: 2 }}>
              <span style={text.eyebrow}>Defender Vulnerability Management ({detail.vulnerabilities.length}{detail.vulnerabilities.length === 200 ? "+" : ""})</span>
              {detail.vulnerabilities.slice(0, 12).map((v) => (
                <span key={`${v.cveId}-${v.productName}`} style={{ color: theme.color.muted }}>
                  <span style={{ ...text.ref, color: theme.color.heading }}>{v.cveId}</span> · {v.severity ?? "—"} · {[v.productVendor, v.productName, v.productVersion].filter(Boolean).join(" ")}{v.fixingKbId ? ` · fix ${v.fixingKbId}` : ""}
                </span>
              ))}
              {detail.vulnerabilities.length > 12 && <span style={{ color: theme.color.faint }}>{detail.vulnerabilities.length - 12} more in Defender</span>}
            </div>
          )}
          {detail && <span style={{ color: theme.color.faint }}>{detail.software.length ? `${detail.software.length}${detail.software.length === 500 ? "+" : ""} installed application${detail.software.length === 1 ? "" : "s"} known` : "No software inventory for this asset yet"} · <PortalLink to="/threat-intelligence?affecting=1" style={{ color: theme.color.accentText, textDecoration: "none", fontWeight: 600 }}>Threat intelligence</PortalLink></span>}
        </div>
      )}
    </div>
  );
}
